import React, { useMemo, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format, formatDistanceToNow, isToday } from 'date-fns'
import { X, Send, Loader2, Paperclip, X as XIcon, MoreHorizontal } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import Avatar from '@/components/Avatar'
import { renderWithMentions, parseMentions, insertMentionToken, removeMentionToken } from '@/lib/mentions'
import DraftMentionPills from '@/components/DraftMentionPills'
import MessageReactions from './MessageReactions'
import MessageAttachmentList from './MessageAttachments'
import { uploadMessageAttachment, deleteMessageAttachment, ATTACHMENT_ACCEPT_ATTR, MAX_ATTACHMENT_BYTES, type MessageAttachment } from '@/lib/messageAttachments'

/**
 * Threads CP2 — the right-side thread drawer.
 *
 * Shows a root message + its replies (oldest→newest) + a reply composer, over a
 * dimmed channel. One-level (Slack-style): a reply's parent_message_id is always
 * the ROOT (the caller passes rootId = m.parent_message_id ?? m.id, so replying
 * to a reply opens the reply's root thread and attaches there).
 *
 * Reuses the shipped leaf components — Avatar, renderWithMentions, MessageReactions
 * (reactions on replies "just work"; they key off team_message_id), and
 * MessageAttachmentList — plus the mention/attachment composer bits. Replies inherit
 * membership RLS via team_conversation_id (CP1), so every query here is RLS-scoped.
 *
 * Realtime for incoming replies is handled by the PARENT's single team-messages sub
 * (it invalidates ['thread-messages', rootId] + the reply-count query), so this
 * drawer just reads the cache.
 */

const MAX_CONTENT_LEN = 4000

interface ThreadMessage {
  id: string
  team_conversation_id: string
  user_id: string | null
  content: string
  created_at: string
  edited_at: string | null
  deleted_at: string | null
  attachments: MessageAttachment[]
  parent_message_id: string | null
  profiles: { full_name: string | null } | null
}

const SELECT_COLS = 'id, team_conversation_id, user_id, content, created_at, edited_at, deleted_at, attachments, parent_message_id, profiles!team_messages_user_fkey(full_name)'

function formatTime(iso: string): string {
  const d = new Date(iso)
  if (isToday(d)) return format(d, 'h:mm a')
  const days = (Date.now() - d.getTime()) / 86400000
  if (days < 7) return format(d, 'EEE h:mm a')
  return format(d, 'MMM d, h:mm a')
}

function ThreadRow({
  m, conversationId, reactions, currentUserId, onlineUserIds, isRoot,
  menuOpen, onToggleMenu, editing, editDraft, onEditDraftChange, onStartEdit, onSaveEdit, onCancelEdit, onDelete, savePending,
}: {
  m: ThreadMessage
  conversationId: string
  reactions: any[]
  currentUserId: string | null
  onlineUserIds: Set<string>
  isRoot?: boolean
  menuOpen: boolean
  onToggleMenu: () => void
  editing: boolean
  editDraft: string
  onEditDraftChange: (v: string) => void
  onStartEdit: () => void
  onSaveEdit: () => void
  onCancelEdit: () => void
  onDelete: () => void
  savePending: boolean
}) {
  const isDeleted = !!m.deleted_at
  const isMine = !!m.user_id && m.user_id === currentUserId
  const senderName = m.profiles?.full_name || 'Unknown user'
  return (
    <div onMouseLeave={() => { if (menuOpen) onToggleMenu() }}
      style={{ display: 'flex', gap: 10, padding: '8px 0', alignItems: 'flex-start', position: 'relative', ...(isRoot ? { borderBottom: '1px solid var(--border-subtle)', paddingBottom: 12, marginBottom: 4 } : {}) }}>
      <Avatar name={senderName} size={32} variant="contact" online={!!m.user_id && onlineUserIds.has(m.user_id)} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)' }}>{senderName}</span>
          <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{formatTime(m.created_at)}</span>
          {!isDeleted && m.edited_at && <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>(edited)</span>}
        </div>
        {isDeleted ? (
          <div style={{ fontSize: 14, color: 'var(--ink-tertiary)', fontStyle: 'italic', marginTop: 2 }}>This message was deleted</div>
        ) : editing ? (
          <div style={{ marginTop: 4 }}>
            <textarea value={editDraft} onChange={e => onEditDraftChange(e.target.value)} autoFocus rows={2}
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onSaveEdit() }
                else if (e.key === 'Escape') { onCancelEdit() }
              }}
              style={{ width: '100%', padding: '8px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 14, color: 'var(--ink-primary)', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <button type="button" onClick={onSaveEdit} disabled={!editDraft.trim() || savePending}
                style={{ padding: '4px 10px', borderRadius: 7, fontSize: 12, fontWeight: 600, background: 'var(--accent)', color: 'var(--accent-ink)', border: 'none', cursor: 'pointer' }}>Save</button>
              <button type="button" onClick={onCancelEdit}
                style={{ padding: '4px 10px', borderRadius: 7, fontSize: 12, fontWeight: 600, background: 'var(--hover-bg)', color: 'var(--ink-secondary)', border: '1px solid var(--border-subtle)', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 14, color: 'var(--ink-secondary)', lineHeight: 1.5, marginTop: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {renderWithMentions(m.content)}
          </div>
        )}
        {!isDeleted && !editing && m.attachments?.length > 0 && <MessageAttachmentList attachments={m.attachments} />}
        {!isDeleted && !editing && (
          <MessageReactions messageId={m.id} conversationId={conversationId} reactions={reactions} currentUserId={currentUserId} />
        )}
      </div>

      {/* Own-message kebab → Edit / Delete (mirrors the main list; soft-delete only) */}
      {isMine && !isDeleted && !editing && (
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <button type="button" aria-label="Message actions" onClick={onToggleMenu}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex', borderRadius: 6 }}>
            <MoreHorizontal style={{ width: 16, height: 16 }} />
          </button>
          {menuOpen && (
            <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 2, background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 9, padding: 4, zIndex: 10, minWidth: 110 }}>
              <button type="button" onClick={onStartEdit}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--ink-primary)', borderRadius: 6 }}>Edit</button>
              <button type="button" onClick={() => { if (window.confirm('Delete this message?')) onDelete() }}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--danger)', borderRadius: 6 }}>Delete</button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function ThreadDrawer({
  rootId, conversationId, workspaceId, currentUserId, currentUserName, reactionsByMessage, convMembers, onlineUserIds, onClose,
}: {
  rootId: string
  conversationId: string
  workspaceId: string
  currentUserId: string
  currentUserName: string | null
  reactionsByMessage: Record<string, any[]>
  convMembers: { id: string; fullName: string }[]
  onlineUserIds: Set<string>
  onClose: () => void
}) {
  const queryClient = useQueryClient()

  // Root message (fetch by id — may not be in the main-list cache; RLS-scoped).
  const { data: root } = useQuery<ThreadMessage | null>({
    queryKey: ['thread-root', rootId],
    queryFn: async () => {
      const { data, error } = await supabase.from('team_messages').select(SELECT_COLS).eq('id', rootId).maybeSingle()
      if (error) throw error
      return (data as any) ?? null
    },
  })

  // Replies (oldest→newest; include tombstones to match the main channel list).
  const { data: replies = [] } = useQuery<ThreadMessage[]>({
    queryKey: ['thread-messages', rootId],
    refetchInterval: 5000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('team_messages')
        .select(SELECT_COLS)
        .eq('parent_message_id', rootId)
        .order('created_at', { ascending: true })
      if (error) throw error
      return (data || []) as any[]
    },
  })

  // ─── Reply composer (own state; mirrors the main composer's minimal bits) ───
  const [input, setInput] = useState('')
  const [showMentions, setShowMentions] = useState(false)
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionIndex, setMentionIndex] = useState(0)
  const [pendingAttachment, setPendingAttachment] = useState<MessageAttachment | null>(null)
  const [uploading, setUploading] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const mentionMatches = useMemo(() => {
    const q = mentionQuery.toLowerCase()
    return convMembers.filter(m => m.fullName.toLowerCase().includes(q)).slice(0, 6)
  }, [convMembers, mentionQuery])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value.slice(0, MAX_CONTENT_LEN)
    setInput(val)
    const caret = e.target.selectionStart ?? val.length
    const mt = val.slice(0, caret).match(/@(\w*)$/)
    if (mt) { setShowMentions(true); setMentionQuery(mt[1]); setMentionIndex(0) } else setShowMentions(false)
  }
  const selectMention = (mb: { id: string; fullName: string }) => {
    const ta = textareaRef.current
    const caret = ta?.selectionStart ?? input.length
    const { value, caret: newCaret } = insertMentionToken(input, caret, mb.fullName, mb.id)
    setInput(value); setShowMentions(false)
    requestAnimationFrame(() => { if (ta) { ta.focus(); ta.setSelectionRange(newCaret, newCaret) } })
  }

  const handlePickFile = async (file: File) => {
    if (file.size > MAX_ATTACHMENT_BYTES) { toast.error('File is too large (max 15MB)'); return }
    setUploading(true)
    const { data, error } = await uploadMessageAttachment(file, workspaceId, conversationId)
    setUploading(false)
    if (error || !data) { toast.error(error?.message || 'Upload failed'); return }
    setPendingAttachment(data)
  }
  const handleRemoveAttachment = () => {
    if (pendingAttachment) deleteMessageAttachment(pendingAttachment.path).catch(() => {})
    setPendingAttachment(null)
  }

  const sendMutation = useMutation({
    mutationFn: async () => {
      const trimmed = input.trim()
      if (!trimmed && !pendingAttachment) throw new Error('Empty')
      const { data, error } = await supabase
        .from('team_messages')
        .insert({
          team_conversation_id: conversationId,
          user_id: currentUserId,
          content: trimmed.slice(0, MAX_CONTENT_LEN),
          attachments: pendingAttachment ? [pendingAttachment] : [],
          parent_message_id: rootId,   // one-level: always the root
        })
        .select('id').single()
      if (error) throw error
      const mentions = parseMentions(trimmed)
      if (data?.id && mentions.length) {
        const rows = mentions.map(mn => ({ team_message_id: data.id, team_conversation_id: conversationId, mentioned_user_id: mn.userId, mentioning_user_id: currentUserId }))
        const { error: mErr } = await (supabase as any).from('message_mentions').insert(rows)
        if (mErr) console.warn('[mentions] insert failed', mErr.message)
      }
    },
    onSuccess: () => {
      setInput(''); setPendingAttachment(null); setShowMentions(false)
      queryClient.invalidateQueries({ queryKey: ['thread-messages', rootId] })
      queryClient.invalidateQueries({ queryKey: ['thread-reply-counts', conversationId] })
      textareaRef.current?.focus()
    },
  })

  const canSend = (!!input.trim() || !!pendingAttachment) && !uploading && !sendMutation.isPending
  const handleSend = () => { if (canSend) sendMutation.mutate() }

  // ─── Edit / soft-delete own thread messages (mirrors the main list exactly) ──
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState('')
  const [menuFor, setMenuFor] = useState<string | null>(null)
  const afterThreadWrite = () => {
    queryClient.invalidateQueries({ queryKey: ['thread-messages', rootId] })
    queryClient.invalidateQueries({ queryKey: ['thread-root', rootId] })   // in case the root itself was edited/deleted
    queryClient.invalidateQueries({ queryKey: ['thread-reply-counts', conversationId] })  // a soft-deleted reply drops from the count
  }
  const editMutation = useMutation({
    mutationFn: async ({ id, content }: { id: string; content: string }) => {
      const trimmed = content.trim()
      if (!trimmed) throw new Error('Empty')
      const { error } = await supabase.from('team_messages')
        .update({ content: trimmed.slice(0, MAX_CONTENT_LEN), edited_at: new Date().toISOString() })
        .eq('id', id).eq('user_id', currentUserId)   // RLS: own only (defense in depth)
      if (error) throw error
    },
    onSuccess: () => { setEditingId(null); setEditDraft(''); afterThreadWrite() },
  })
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // SOFT-delete (tombstone) — same as the main list. NOT a hard delete: a hard
      // delete has no authenticated RLS path and would orphan replies.
      const { error } = await supabase.from('team_messages')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id).eq('user_id', currentUserId)
      if (error) throw error
    },
    onSuccess: () => { setMenuFor(null); afterThreadWrite() },
  })

  const rowProps = (m: ThreadMessage) => ({
    menuOpen: menuFor === m.id,
    onToggleMenu: () => setMenuFor(menuFor === m.id ? null : m.id),
    editing: editingId === m.id,
    editDraft,
    onEditDraftChange: setEditDraft,
    onStartEdit: () => { setEditingId(m.id); setEditDraft(m.content); setMenuFor(null) },
    onSaveEdit: () => editMutation.mutate({ id: m.id, content: editDraft }),
    onCancelEdit: () => { setEditingId(null); setEditDraft('') },
    onDelete: () => deleteMutation.mutate(m.id),
    savePending: editMutation.isPending,
  })

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', zIndex: 90, display: 'flex', justifyContent: 'flex-end' }}
    >
      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'tween', duration: 0.2 }}
        onClick={e => e.stopPropagation()}
        style={{ width: 'min(440px, 100vw)', height: '100%', background: 'var(--surface)', borderLeft: '1px solid var(--border-default)', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <h2 style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>Thread</h2>
            <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '2px 0 0' }}>{replies.length} {replies.length === 1 ? 'reply' : 'replies'}</p>
          </div>
          <button onClick={onClose} aria-label="Close thread" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex' }}>
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {/* Root + replies */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 20px' }}>
          {root && (
            <ThreadRow m={root} conversationId={conversationId} reactions={reactionsByMessage[root.id] || []} currentUserId={currentUserId} onlineUserIds={onlineUserIds} isRoot {...rowProps(root)} />
          )}
          {replies.map(r => (
            <ThreadRow key={r.id} m={r} conversationId={conversationId} reactions={reactionsByMessage[r.id] || []} currentUserId={currentUserId} onlineUserIds={onlineUserIds} {...rowProps(r)} />
          ))}
        </div>

        {/* Reply composer */}
        <div style={{ padding: '12px 20px 16px', borderTop: '1px solid var(--border-subtle)', flexShrink: 0, position: 'relative' }}>
          {showMentions && mentionMatches.length > 0 && (
            <div style={{ position: 'absolute', bottom: '100%', left: 20, right: 20, marginBottom: 8, background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 12, overflow: 'hidden', zIndex: 50, maxHeight: 240 }}>
              <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)', fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)' }}>Mention a teammate · ↑↓ then Enter</div>
              <div style={{ maxHeight: 200, overflow: 'auto' }}>
                {mentionMatches.map((mb, i) => (
                  <button key={mb.id} type="button" onMouseDown={e => { e.preventDefault(); selectMention(mb) }} onMouseEnter={() => setMentionIndex(i)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: i === mentionIndex ? 'var(--accent-dim)' : 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                    <Avatar name={mb.fullName} size={22} variant="contact" />
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' }}>{mb.fullName}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <DraftMentionPills content={input} onRemove={uid => setInput(removeMentionToken(input, uid))} style={{ marginBottom: 8 }} />
          {(pendingAttachment || uploading) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '6px 10px', background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 10 }}>
              {uploading ? (
                <><Loader2 style={{ width: 14, height: 14, color: 'var(--ink-tertiary)' }} className="animate-spin" /><span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>Uploading…</span></>
              ) : pendingAttachment && (
                <>
                  <Paperclip style={{ width: 14, height: 14, color: 'var(--ink-tertiary)', flexShrink: 0 }} />
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, fontWeight: 600, color: 'var(--ink-primary)' }}>{pendingAttachment.name}</span>
                  <button type="button" onClick={handleRemoveAttachment} aria-label="Remove attachment" style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 2, display: 'flex' }}><XIcon style={{ width: 14, height: 14 }} /></button>
                </>
              )}
            </div>
          )}
          <input ref={fileInputRef} type="file" accept={ATTACHMENT_ACCEPT_ATTR} onChange={e => { const f = e.target.files?.[0]; if (f) handlePickFile(f); e.target.value = '' }} style={{ display: 'none' }} />
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 12, padding: '8px 10px' }}>
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading || !!pendingAttachment} aria-label="Attach file"
              style={{ background: 'none', border: 'none', cursor: (uploading || pendingAttachment) ? 'not-allowed' : 'pointer', color: 'var(--ink-tertiary)', padding: 8, display: 'flex', borderRadius: 8, flexShrink: 0, opacity: (uploading || pendingAttachment) ? 0.4 : 1 }}>
              <Paperclip style={{ width: 16, height: 16 }} />
            </button>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={handleChange}
              onKeyDown={e => {
                if (showMentions && mentionMatches.length > 0) {
                  if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex(i => (i + 1) % mentionMatches.length); return }
                  if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex(i => (i - 1 + mentionMatches.length) % mentionMatches.length); return }
                  if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); selectMention(mentionMatches[mentionIndex]); return }
                  if (e.key === 'Escape') { e.preventDefault(); setShowMentions(false); return }
                }
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
              }}
              placeholder="Reply…"
              rows={2}
              disabled={sendMutation.isPending}
              style={{ flex: 1, minHeight: 40, maxHeight: 120, resize: 'none', background: 'transparent', border: 'none', outline: 'none', color: 'var(--ink-primary)', fontSize: 14, lineHeight: 1.5, fontFamily: 'inherit', padding: '4px 2px' }}
            />
            <button onClick={handleSend} disabled={!canSend}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '9px 12px', borderRadius: 10, border: 'none', cursor: canSend ? 'pointer' : 'not-allowed', background: canSend ? 'var(--accent)' : 'var(--hover-bg)', color: canSend ? 'var(--accent-ink)' : 'var(--ink-tertiary)', flexShrink: 0 }} title="Send (Enter)">
              {sendMutation.isPending ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" /> : <Send style={{ width: 14, height: 14 }} />}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}
